import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MIN_REVIEWS_FOR_AVERAGE, canShowAverage, formatRating } from '../core/domain/reviews';

/**
 * Note d'un producteur ou d'un produit.
 *
 * Ce composant est le seul endroit autorisé à afficher une note moyenne : il
 * applique le seuil de 3 avis du dossier, en dessous duquel la moyenne reste
 * masquée pour ne pas condamner un producteur qui démarre sur un seul client
 * mécontent. Passer par un composant plutôt que par une interpolation dans
 * chaque écran garantit que la règle ne peut pas être contournée par oubli.
 */
@Component({
  selector: 'ct-rating',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (showAverage()) {
      <span class="rating" [attr.aria-label]="label()">
        <span class="star" aria-hidden="true">&#9733;</span>
        <span class="value">{{ formatted() }}</span>
        <span class="count">({{ count() }})</span>
      </span>
    } @else {
      <span class="pending">{{ label() }}</span>
    }
  `,
  styles: `
    .rating,
    .pending {
      display: inline-flex;
      align-items: baseline;
      gap: 0.2rem;
      font-size: var(--ct-size-sm);
      color: var(--ct-text-muted);
    }

    .star {
      color: var(--ct-or-recolte-dark);
    }

    .value {
      font-weight: var(--ct-weight-bold);
      color: var(--ct-text);
    }
  `,
})
export class CtRating {
  /** Moyenne sur 5. Ignorée tant que le seuil d'avis n'est pas atteint. */
  readonly average = input<number | undefined>(undefined);

  readonly count = input.required<number>();

  protected readonly showAverage = computed(
    () => canShowAverage(this.count()) && this.average() !== undefined,
  );

  protected readonly formatted = computed(() => {
    const average = this.average();
    return average === undefined ? '' : formatRating(average);
  });

  /**
   * Texte annoncé aux lecteurs d'écran, et texte visible quand la moyenne est
   * encore masquée. On dit explicitement à partir de quand la note apparaîtra :
   * un « aucun avis » sec laisserait croire à un producteur peu fiable plutôt
   * qu'à un producteur récent.
   */
  protected readonly label = computed(() => {
    const count = this.count();
    if (this.showAverage()) {
      return `Note ${this.formatted()} sur 5, ${count} avis`;
    }
    if (count === 0) {
      return 'Pas encore d’avis';
    }
    return `${count} avis — note affichée à partir de ${MIN_REVIEWS_FOR_AVERAGE}`;
  });
}
